/**
 * The realtime contract.
 *
 * One envelope for every server event, so the client has exactly one place to
 * handle ordering and exactly one place to bridge into its query cache. The
 * two ordering defences live in the envelope rather than in either handler:
 *
 *  - `rev` is the conversation's `updated_at` in epoch milliseconds. A patch
 *    whose rev is not newer than what the client holds is dropped — that is
 *    what makes an out-of-order broadcast harmless instead of a rollback.
 *  - `seq` is a per-process counter. A jump means packets were missed (proxy
 *    hiccup, a slow tab), and the client refetches rather than guessing. It
 *    resets when the server restarts, but a restart also drops every socket,
 *    and the reconnect path already forces a resync.
 */

import { z } from 'zod';
import {
  boardRowSchema,
  conversationSummarySchema,
  messageSchema,
  parkedRowSchema,
  parkReasonSchema,
  userSummarySchema,
} from './types.js';

/** The room every signed-in tab joins. Carries board and queue changes. */
export const BOARD_ROOM = 'board';

/** The room a tab joins while a thread is open. One per conversation. */
export function conversationRoom(conversationId: string): string {
  return `conversation:${conversationId}`;
}

export const serverEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('message.created'),
    seq: z.number(),
    rev: z.number(),
    conversationId: z.string(),
    message: messageSchema,
    /** Board rows show the newest snippet without opening the thread. */
    row: boardRowSchema,
  }),
  z.object({
    type: z.literal('conversation.updated'),
    seq: z.number(),
    rev: z.number(),
    conversationId: z.string(),
    conversation: conversationSummarySchema,
    row: boardRowSchema,
  }),
  z.object({
    type: z.literal('conversation.claimed'),
    seq: z.number(),
    rev: z.number(),
    conversationId: z.string(),
    /** Who won. This is what removes the row from everyone else's queue. */
    by: userSummarySchema,
    row: boardRowSchema,
  }),
  z.object({
    type: z.literal('conversation.parked'),
    seq: z.number(),
    rev: z.number(),
    conversationId: z.string(),
    reason: parkReasonSchema,
    summary: z.string().nullable(),
    row: parkedRowSchema,
  }),
  z.object({
    type: z.literal('conversation.handled_back'),
    seq: z.number(),
    rev: z.number(),
    conversationId: z.string(),
    by: userSummarySchema,
    row: boardRowSchema,
  }),
]);
export type ServerEvent = z.infer<typeof serverEventSchema>;
export type ServerEventType = ServerEvent['type'];

/**
 * Socket.IO's typed-events maps. One `event` channel rather than one channel
 * per type: the client then has a single listener, which is the only way the
 * sequence check above can see every packet.
 */
export interface ServerToClientEvents {
  event: (e: ServerEvent) => void;
}

export interface ClientToServerEvents {
  'thread:watch': (conversationId: string) => void;
  'thread:unwatch': (conversationId: string) => void;
}

/** What the server hangs off an authenticated socket. */
export interface SocketData {
  userId: string;
  name: string;
  role: 'admin' | 'agent';
}
